import {match, Pattern} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {
  type GetCustomerV2ApiResponse,
  LegalFormSchema,
  type PersonResponseBodyV2,
} from '@dms/api/customer';
import {getNaturalPersonFullName, isCustomerNaturalPerson} from '@dms/shared';

import type {Nullish} from 'shared/web';

import type {DefaultSignatoriesFormData} from '../types/DefaultSignatoriesFormData';
import {getTrimmedFormValue} from './getTrimmedFormValue';

type CustomerSignatoryDefaults = DefaultSignatoriesFormData[string];

const createSignatory = (
  customerId: string,
  name: string | Nullish,
  person: Pick<PersonResponseBodyV2, 'emails' | 'phoneNumbers'> | Nullish
): CustomerSignatoryDefaults => {
  const phoneNumber = person?.phoneNumbers[0];

  return {
    id: customerId,
    name: name ?? '',
    email: person?.emails[0]?.email ?? null,
    phoneNumber: {
      countryCode: getTrimmedFormValue(phoneNumber?.countryCode),
      number: getTrimmedFormValue(phoneNumber?.number),
      prefix: getTrimmedFormValue(phoneNumber?.prefix),
    },
  };
};

/**
 * Pre-fills the customer signatory of a consent given without a contact person from the billing
 * info it was given on: the person of a natural-person billing info, and for self-employed billing
 * info the same data as the consent's CUSTOMER_* placeholders. A missing person or field is left
 * for the operator to fill in, so that nobody else's contact details are pre-filled. Returns null
 * for legal-entity billing info, whose consents are signed by the contact person they name, so a
 * consent without one keeps the customer defaults.
 */
export const getContractInformationSignatoryDefaults = (
  customer: GetCustomerV2ApiResponse | Nullish,
  contractInformationId: string
): CustomerSignatoryDefaults | null => {
  if (isNil(customer)) {
    return null;
  }

  const contractInformation = customer.contractInformation.find(
    (item) => item.id === contractInformationId
  );
  const primaryContact = customer.contacts.find(
    (contact) => contact.id === customer.primaryContactId
  );

  return match({
    contractInformation,
    isNaturalPerson: isCustomerNaturalPerson(customer),
    foundingPerson: customer.foundingPerson,
  })
    .with(
      {
        contractInformation: {
          legalForm: LegalFormSchema.enum.NATURAL_PERSON,
          person: Pattern.nonNullable,
        },
      },
      ({contractInformation}) =>
        createSignatory(
          customer.id,
          getNaturalPersonFullName(contractInformation.person),
          contractInformation.person
        )
    )
    .with(
      {
        contractInformation: {legalForm: LegalFormSchema.enum.SELF_EMPLOYED},
        isNaturalPerson: true,
        foundingPerson: Pattern.nonNullable,
      },
      ({foundingPerson}) =>
        createSignatory(customer.id, getNaturalPersonFullName(foundingPerson), foundingPerson)
    )
    .with(
      {contractInformation: {legalForm: LegalFormSchema.enum.SELF_EMPLOYED}},
      ({contractInformation}) =>
        createSignatory(
          customer.id,
          contractInformation.businessInfo?.businessInfo.tradeName,
          primaryContact
        )
    )
    .with({contractInformation: {legalForm: LegalFormSchema.enum.LEGAL_ENTITY}}, always(null))
    .otherwise(() => ({
      id: customer.id,
      name: null,
      email: null,
      phoneNumber: {countryCode: '', number: '', prefix: ''},
    }));
};
